import { baseUrlEndpoint, baseUrlPattern, resolveBaseUrl } from "../baseUrl";
import {
  connectorAllowedModels,
  geminiModelsExtract,
  MODELS_HELPER_TEXT,
  MODELS_PREFIX,
  withAllowedModelsHint,
} from "../models";
import { queryAuth } from "../standardAuthPatterns";
import { defineConnector, type ListFieldFetch } from "../types";
import logo from "./logo.svg";
import { geminiUsage } from "./usage";

const DEFAULT_GEMINI_BASE_URL =
  "https://generativelanguage.googleapis.com/v1beta";

// Gemini pages `/models` at 50 by default and already lists more than that, so
// the picker asks for the maximum page rather than silently truncating.
const geminiModelsFetch: ListFieldFetch = {
  label: "Fetch models from Google",
  request: ({ baseUrl }) =>
    baseUrlEndpoint(baseUrl, DEFAULT_GEMINI_BASE_URL, "/models?pageSize=1000"),
  extract: geminiModelsExtract,
};

// Gemini names models in the path, so the proxy can only enforce an allowlist
// on requests that carry one; everything else fails closed while a list is set.
const GEMINI_RESTRICTED_ENDPOINTS =
  "file uploads, cached contents, and batch requests are rejected, because they don’t name a model the proxy can check";

const GEMINI_MODELS_HELPER_TEXT = `${MODELS_HELPER_TEXT} While a list is set, ${GEMINI_RESTRICTED_ENDPOINTS}.`;

export const googleGemini = defineConnector({
  identifier: "googleGemini",
  name: "Google Gemini",
  tagLine: "Multimodal AI platform",
  keywords: "bard palm ai studio generative language llm models multimodal",
  logo,
  protocol: "http",
  tags: ["featured", "ai", "engineering"],
  defaultUrlPattern: ({ baseUrl }) =>
    baseUrlPattern(baseUrl, DEFAULT_GEMINI_BASE_URL),
  auth: (app) => queryAuth([{ name: "key", value: app.secret.apiKey }]),
  steps: [
    {
      title: "Get an API key",
      items: [
        {
          kind: "markdown",
          content:
            "Visit [Google AI Studio](https://aistudio.google.com/apikey) and create or copy an API key.",
        },
      ],
    },
    {
      title: "Copy and paste the API key into the field below",
      items: [
        {
          kind: "input",
          label: "Google Gemini API key",
          placeholder: "AIza...",
          required: true,
          target: { kind: "secrets", field: "apiKey" },
        },
        {
          kind: "url",
          label: "Base URL (optional)",
          placeholder: DEFAULT_GEMINI_BASE_URL,
          defaultValue: DEFAULT_GEMINI_BASE_URL,
          target: { kind: "configuration", field: "baseUrl" },
          helperText: "Leave blank for the default Gemini API endpoint.",
        },
      ],
    },
    {
      title: "Choose available models (optional)",
      items: [
        {
          kind: "list",
          label: "Available models",
          prefix: MODELS_PREFIX,
          columns: [{ field: "name", placeholder: "gemini-2.5-pro" }],
          addLabel: "Add model",
          fetch: geminiModelsFetch,
          helperText: GEMINI_MODELS_HELPER_TEXT,
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl }) =>
      baseUrlEndpoint(baseUrl, DEFAULT_GEMINI_BASE_URL, "/models"),
  },
  publicEnvVars: ({ baseUrl }) => ({
    GOOGLE_GEMINI_BASE_URL: resolveBaseUrl(baseUrl, DEFAULT_GEMINI_BASE_URL),
  }),
  usageHint: (configuration) => {
    const hint = withAllowedModelsHint(
      "Use process.env.GOOGLE_GEMINI_BASE_URL as the Gemini API base URL. Pass a placeholder API key; the proxy injects the real key from the connected app.",
      configuration
    );
    return connectorAllowedModels(configuration).length
      ? `${hint} Also, ${GEMINI_RESTRICTED_ENDPOINTS}.`
      : hint;
  },
  usage: geminiUsage(),
});
